import React, { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';

import { exportWorkspaceTxt, exportWorkspaceCsv } from 'Shared/api/workspaces';
import { showNotificationError, downloadFile } from 'Shared/helpers';
import WpButton from 'Wpds/components/button/WpButton';
import WpButtonGroup from 'Wpds/components/button/WpButtonGroup';
import CustomDateRange from 'Shared/components/CustomDateRange/CustomDateRange';
import { WpModal, WpModalHeader, WpModalBody, WpModalFooter } from 'Wpds/components/modal';

import { WpCollapsible } from 'Wpds/components/collapsible';
import { WpListSelect, ListSelectOption } from 'Wpds/components/list';
import { WpFormLayout } from 'Wpds/components/form';
import WpSelectV2 from 'Wpds/components/select/WpSelectV2';

import './ExportWorkspaceModal.scss';

type Props = {
  onToggle(): void;
};

const fileFormatOptions = [
  { label: 'TXT', value: 'txt' },
  { label: 'CSV', value: 'csv' },
];

const timeRangeOptions = [
  {
    label: 'Today',
    value: 0,
  },
  {
    label: 'Last 7 days',
    value: 7,
  },
  {
    label: 'Last 14 days',
    value: 14,
  },
  {
    label: 'Last 30 days',
    value: 30,
  },
  {
    label: 'Choose specific timerange',
    value: 'custom',
  },
] as ListSelectOption[];

function ExportWorkspaceModal({ onToggle }: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fileFormat, setFileFormat] = useState(null);
  const workspaceId = useSelector(state => state.workspace.id);
  const [selectedRangeOptions, setSelectedRangeOptions] = useState([timeRangeOptions[0]]);
  const [customRangeValue, setCustomRangeValue] = useState({ startRange: null, endRange: null });

  // @ts-ignore
  const isCustom = selectedRangeOptions[0]?.value === 'custom';

  const handleCancelClick = () => onToggle();

  const handleCustomRangeValueChange = useCallback(value => setCustomRangeValue(value), []);

  const handleSelectChange = value => setFileFormat(value);

  const exportTxt = async ({ startDate, endDate }) => {
    const response = await exportWorkspaceTxt({ workspaceId, startDate, endDate });
    if (response && response.status === 200) {
      const file = new Blob([response.data.text], { type: 'text/plain' });
      downloadFile({ file, filename: response.data.filename });
    }
  };

  const exportCsv = async ({ startDate, endDate }) => {
    const response = await exportWorkspaceCsv({ workspaceId, startDate, endDate });
    if (response && response.status === 200) {
      const file = new Blob([response.data.text], { type: 'text/csv;charset=utf-8;' });
      downloadFile({ file, filename: response.data.filename });
    }
  };

  const handleSubmit = async () => {
    let startDate;
    let endDate;

    if (isCustom) {
      startDate = customRangeValue.startRange.unix();
      endDate = customRangeValue.endRange.unix();
    } else {
      endDate = moment().unix();
      // @ts-ignore
      startDate = moment().subtract(selectedRangeOptions[0].value, 'days').unix();
    }
    try {
      setIsSubmitting(true);
      if (fileFormat === 'txt') {
        await exportTxt({ startDate, endDate });
      } else {
        await exportCsv({ startDate, endDate });
      }
    } catch (e) {
      showNotificationError(e);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSubmitDisabled = isSubmitting || !fileFormat;

  return (
    <WpModal isOpen size="sm" onToggle={onToggle}>
      <WpModalHeader>Export Recent Activity</WpModalHeader>
      <WpModalBody>
        <div className="ExportWorkspaceModal">
          <div className="mb-3">Export recent activity to a .csv or .txt to quickly share progress reports</div>
          <div className="ExportWorkspaceModal__subtitle mb-1">Would you like to export as csv or txt?</div>
          <WpFormLayout>
            <WpSelectV2
              id="fileFormat"
              placeholder="Select format"
              label="File Format"
              options={fileFormatOptions}
              labelHidden
              onChange={handleSelectChange}
            />
          </WpFormLayout>
          <div className="ExportWorkspaceModal__subtitle mb-1">What date range do you like to export?</div>
          <WpListSelect
            id="export-range"
            selectedOptions={selectedRangeOptions}
            options={timeRangeOptions}
            iconNotSelected="circle"
            max={1}
            onChange={setSelectedRangeOptions}
            disableOnMax={false}
          />

          <WpCollapsible open={isCustom}>
            <CustomDateRange
              startRange={customRangeValue.startRange}
              endRange={customRangeValue.endRange}
              onRangeChange={handleCustomRangeValueChange}
              withPortal
            />
          </WpCollapsible>
        </div>
      </WpModalBody>
      <WpModalFooter>
        <WpButtonGroup>
          <WpButton variant="tertiary" onClick={handleCancelClick}>
            Cancel
          </WpButton>
          <WpButton onClick={handleSubmit} disabled={isSubmitDisabled} loading={isSubmitting}>
            Submit
          </WpButton>
        </WpButtonGroup>
      </WpModalFooter>
    </WpModal>
  );
}

export default ExportWorkspaceModal;
